// BuyerHero.jsx
import { useEffect, useState } from 'react'
import { useLanguage } from '../../context/LanguageContext'
import api from '../../lib/api.ts'
import { StepByStepGuide } from './step-by-step-guide.jsx'
import { ConfidenceSection } from './confidence-section.jsx'

export default function BuyerHero() {
  const { language, translate } = useLanguage()

  const [hero, setHero] = useState({
    title: '',           // main title (white)
    accentTitle: '',     // highlighted part (blue)
    subtitle: '',        // description below title
    btnText: '',
    btnLink: '',
    imageUrl: '',
  })

  const [loading, setLoading] = useState(true)
  const [registrationSteps, setRegistrationSteps] = useState([])
  const [stepsHeader, setStepsHeader] = useState({ title: '', description: '' })

  const [confidenceHeader, setConfidenceHeader] = useState({ title: '', description: '' })
  const [confidenceSteps, setConfidenceSteps] = useState([])

  useEffect(() => {
    const fetchPageSettings = async () => {
      try {
        setLoading(true)
        const response = await api.get('/pagesettings/clientbuyerpage', {
          params: { lang: language },
        })

        const data = response.data || {}

        // Hero Banner
        const banner = data.hero_banner?.[0] || {}

        setHero({
          title: banner.title?.value?.trim() || translate('newBuyer.hero.title') || 'Become a Buyer Today',
          accentTitle: banner.accent_title?.value?.trim() || banner.subtitle?.value?.trim() || translate('newBuyer.hero.accent') || 'Fast. Secure. Transparent.',
          subtitle: banner.description?.value?.trim() || banner.subtitle_2?.value?.trim() || '', // ← use correct field if API has separate description
          btnText: banner.btnText?.value?.trim() || translate('newBuyer.hero.button') || 'Become a Buyer',
          btnLink: banner.btnLink?.value?.trim() || '/login',
          imageUrl: banner.imageUrl?.value || '',
        })

        // Registration Steps Header
        const headerData = data.registration_steps_header?.[0] || {}
        setStepsHeader({
          title: headerData.title?.value || translate('newBuyer.stepByStep.title') || 'How It Works',
          description: headerData.description?.value || '',
        })

        setRegistrationSteps(data.registration_steps || [])

        // Confidence Section
        const confHeaderData = data.confidence_header?.[0] || {}
        setConfidenceHeader({
          title: confHeaderData.title?.value || translate('newBuyer.confidence.title') || 'Buy with Absolute Confidence',
          description: confHeaderData.description?.value || '',
        })

        setConfidenceSteps(data.confidence_steps || [])
      } catch (error) {
        console.error('❌ Error fetching buyer page settings:', error)

        // Fallbacks
        setHero({
          title: translate('newBuyer.hero.title') || 'Become a Buyer Today',
          accentTitle: translate('newBuyer.hero.accent') || 'Join Al Qaryah Auctions',
          subtitle: '',
          btnText: translate('newBuyer.hero.button') || 'Become a Buyer',
          btnLink: '/login',
          imageUrl: '',
        })

        setStepsHeader({
          title: translate('newBuyer.stepByStep.title') || 'How It Works',
          description: '',
        })
        setRegistrationSteps([])

        setConfidenceHeader({
          title: translate('newBuyer.confidence.title') || 'Buy with Absolute Confidence',
          description: '',
        })
        setConfidenceSteps([])
      } finally {
        setLoading(false)
      }
    }

    fetchPageSettings()
  }, [language, translate])

  const backgroundStyle = hero.imageUrl
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.62), rgba(0,0,0,0.78)), url(${hero.imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : {
        background: 'linear-gradient(180deg, #000000 0%, #0f172a 100%)',
      }

  if (loading) {
    return (
      <section className="relative overflow-hidden pt-20 pb-20" style={backgroundStyle}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="h-8 w-64 bg-gray-700/30 rounded animate-pulse mx-auto mb-6" />
          <div className="h-20 w-4/5 bg-gray-700/30 rounded animate-pulse mx-auto mb-6" />
          <div className="h-6 w-2/3 bg-gray-700/30 rounded animate-pulse mx-auto" />
        </div>
      </section>
    )
  }

  return (
    <>
      {/* <section className="relative overflow-hidden pt-20 lg:pt-28 pb-16 sm:pb-24" style={backgroundStyle}> */}
         <section className="relative overflow-hidden pt-20 lg:pt-28 pb-16 sm:pb-24 bg-gradient-to-b from-primary-dark/80 to-primary-background"s>
        {/* Ambient glows */}
        <div className="absolute top-[-15%] left-[-10%] w-[600px] h-[600px] bg-blue-600/10 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-15%] w-[700px] h-[700px] bg-blue-500/8 blur-[160px] rounded-full pointer-events-none" />

        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center relative z-10">
          {/* Badge */}
          <div className="inline-block px-5 py-2 rounded-full bg-primary/10 border border-primary/25 mb-8 sm:mb-10">
            <p className="text-secondary text-xs sm:text-sm font-black tracking-[0.25em] uppercase">
              {translate('newBuyer.hero.badge') || 'Simple • Secure • Trusted'}
            </p>
          </div>

          {/* Titles */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.08] tracking-tight mb-6 lg:mb-8">
            <span className="text-white block">{hero.title}</span>
            <span className="text-primary">{hero.accentTitle}</span>
          </h1>

          {/* Subtitle / Description – now visible */}
          {hero.subtitle && (
            <p className="text-gray-200 text-lg sm:text-xl max-w-3xl mx-auto font-medium leading-relaxed mb-10 lg:mb-14">
              {hero.subtitle}
            </p>
          )}

          {/* Button – you might want to add it back */}
          {/* {hero.btnText && (
            <a
              href={hero.btnLink}
              className="inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-2xl transition-all shadow-lg shadow-blue-900/30 hover:shadow-xl hover:shadow-blue-900/40 active:scale-[0.98]"
            >
              {hero.btnText}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          )} */}
        </div>
      </section>

      <StepByStepGuide registrationSteps={registrationSteps} header={stepsHeader} />

      <ConfidenceSection header={confidenceHeader} steps={confidenceSteps} translate={translate} />
    </>
  )
}